import React from "react";
import { NotificationContainer } from "react-notifications";
import "react-notifications/lib/notifications.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import IdleTimerWrapper from "./components/IdleTimer/IdleTimerWrapper";
import { AuthProvider } from "./contexts/auth";
import { ListManagerProvider } from "./contexts/list.manager";
import { SSEProvider } from "./contexts/sse";
import { ThemeProvider } from "./contexts/theme";
import { UserStoreProvider } from "./contexts/userStore";
import AppRoutes from "./routes/routes-identity";
import { BackupProvider } from "./contexts/backup";
import { IndexedDbProvider } from "./contexts/indexedDb";
import { NotificationsProvider } from "./contexts/notifications";
import { ToastContainer } from "react-toastify";
import { AlarmsProvider } from "./contexts/alarms";
import Loader from "./components/Loader/Loader";
import { v4 as uuidv4 } from "uuid";
import { BrowserTabAlert } from "./components/BrowserTabAlert/BrowserTabAlert";
import { minimatch } from "minimatch";
import { PublicInvitation } from "./views/Invitations/PublicInvitation";
import "./i18n";
import { TooltipProvider } from "./components/shadcn/tooltip";
import { NetworkInfo } from "./components/NetworkInfo/NetworkInfo";

const IDLE_TIMEOUT = 30 * 60 * 1000;
const uniqid = uuidv4();
const publicUrls = ['/invitation/**'];

function MultiTabsChecker({ children }) {
	const [isAnotherTabOpen, setIsAnotherTabOpen] = React.useState(false);
	const [isTabChecked, setIsTabChecked] = React.useState(false);

	React.useEffect(() => {
		const isPublicUrl = publicUrls.some((url) => minimatch(window.location.pathname, url));
		if (isPublicUrl) {
			return;
		}

		const channel = new BroadcastChannel("tab-checker");

		channel.postMessage("check-tabs");

		channel.onmessage = (event) => {
			if (event.data.startsWith("tab-open-") && !event.data.includes(uniqid)) {
				setIsAnotherTabOpen(true);
				setIsTabChecked(true);
				document.querySelector("#favicon").setAttribute("href", "/favicon-alert.png");
			}
		};

		const timeoutId = setTimeout(() => {
			if (!isTabChecked) {
				setIsTabChecked(true);
			}
		}, 1200);

		return () => {
			channel.close();
			clearTimeout(timeoutId);
		};
	}, []);

	React.useEffect(() => {
		const isPublicUrl = publicUrls.some((url) => url.includes(window.location.pathname));
		if (isPublicUrl) {
			return;
		}

		if (isTabChecked && !isAnotherTabOpen) {
			const channel = new BroadcastChannel("tab-checker");
			channel.postMessage("tab-open-" + uniqid);
			return () => channel.close();
		}
	}, [isTabChecked, isAnotherTabOpen]);

	if (!isTabChecked) {
		return (
			<div style={{ padding: "20px", textAlign: "center" }}>
				<Loader />
			</div>
		);
	}

	if (isAnotherTabOpen) {
		return <BrowserTabAlert />;
	}

	return children;
}

function App() {

	const isPublicUrl = publicUrls.some((url) => minimatch(window.location.pathname, url));
	if (isPublicUrl) {
		return (
			<IndexedDbProvider>
				<ThemeProvider>
					<div style={{ height: "100vh" }}>
						<BrowserRouter>
							<Routes>
								<Route path="/invitation/token/:token" element={<PublicInvitation />} />
							</Routes>
							<ToastContainer />
						</BrowserRouter>
						<NetworkInfo />
					</div>
				</ThemeProvider>
			</IndexedDbProvider>
		);
	}

	return (
		<div style={{ height: "100vh" }}>
			<CombinedProviders>
				<NetworkInfo />
				<BrowserRouter>
					<IdleTimerWrapper timeout={IDLE_TIMEOUT} />
					<AppRoutes />
					<ToastContainer />
				</BrowserRouter>
				<NotificationContainer />
			</CombinedProviders>
		</div>
	);
}

function CombinedProviders(props) {
	return (
		<AuthProvider>
			<UserStoreProvider>
				<SSEProvider>
					<BackupProvider>
						<NotificationsProvider>
							<IndexedDbProvider>
								<ThemeProvider>
									<MultiTabsChecker>
										<AlarmsProvider>
											<TooltipProvider>
												<ListManagerProvider>
													{props.children}
												</ListManagerProvider>
											</TooltipProvider>
										</AlarmsProvider>
									</MultiTabsChecker>
								</ThemeProvider>
							</IndexedDbProvider>
						</NotificationsProvider>
					</BackupProvider>
				</SSEProvider>
			</UserStoreProvider>
		</AuthProvider>
	);
}

export default App;
